import {LogoType} from "../../components/constants/securspace-constants";
import {ObservableVariables, IObservableVariable} from "../../components/constants/observable-constants";

export type Nav = {
  //badge?: string;
  badgeObservable?: IObservableVariable;
  exact: boolean;
  external?: boolean;
  highlighted: boolean;
  label: string;
  logoType: string;
  path: string;
  urlParams?: string;
};

const defaultNavConfig = {
  highlighted: false,
  logoType: LogoType.LOGO_TRANSPARENT,
  exact: false
};

interface NavConfigInput {
  path: string;
  label: string;
}

const makeNavConfig = (config: NavConfigInput): Nav => {
  return {
    ...defaultNavConfig,
    ...config
  };
};

interface OptionalNavConfig {
  //badge?: string;
  badgeObservable?: IObservableVariable;
  urlParams?: string;
  exact?: boolean;
  external?: boolean;
  highlighted?: boolean;
  logoType?: string;
}

const makeNav = (path: string, label: string, config?: OptionalNavConfig): Nav => {
  let newConfig: NavConfigInput & OptionalNavConfig = {path, label};
  if (config) {
    newConfig = {...newConfig, ...config};
  }
  return makeNavConfig(newConfig);
};

export const MenuItemUrlParams = Object.freeze({
  BUYER_BOOKING: 'buyer-booking',
});

export const LandingNav = Object.freeze({
  LIST_YOUR_LOCATION: makeNav('supplier-signup', 'List Your Location'),
  SEARCH: makeNav('yards', 'Search'),
  ABOUT: makeNav('about', 'About'),
  CONTACT: makeNav('contact', 'Contact'),
  FAQ: makeNav('faq', 'Faq'),
  LOG_IN: makeNav('login', 'Log In'),
  REGISTER_AS_CUSTOMER: makeNav('buyer-signup', 'Register as Customer'),
  REGISTER_AS_PARTNER: makeNav('supplier-signup', 'Register as Partner'),
  RESOURCES_AS_BUYER: makeNav('buyer-resources', 'Support'),
  RESOURCES_AS_SUPPLIER: makeNav('supplier-resources', 'Support'),
  EMAIL_VERIFICATION: makeNav('verify', 'Verify Registration'),
});

export const LandingNavHome = Object.freeze({
  LIST_YOUR_LOCATION: makeNav('supplier-signup', 'List Your Location'),
  SEARCH: makeNav('yards', 'Search'),
  ABOUT: makeNav('about', 'About'),
  HOW_IT_WORKS: makeNav('#how-it-works', 'How It Works'),
  CONTACT: makeNav('contact', 'Contact'),
  FAQ: makeNav('faq', 'Faq'),
  LOG_IN: makeNav('login', 'Log In'),
  REGISTER_AS_CUSTOMER: makeNav('buyer-signup', 'Register as Customer'),
  REGISTER_AS_PARTNER: makeNav('supplier-signup', 'Register as Partner'),
});

export const AccountNav = Object.freeze({
  COMPANY_PROFILE: makeNav('company-profile', 'Company'),
  LOCATIONS_BUYER_SUMMARY: makeNav('account/my-locations', 'My Locations'),
  PAYMENT_METHODS_BUYER: makeNav('payment-methods', 'Payment Methods'),
  PAYOUT_ACCOUNT_PARTNER: makeNav('payment-methods', 'Payout Bank Account'),
  LOCATIONS_PROFILE: makeNav('locations-profile', 'Locations'),
  API_INTEGRATION: makeNav('api-integration', 'API Integration'),
  USER_MANAGEMENT: makeNav('user-management', 'Users'),
  NOTIFICATIONS_SETTINGS: makeNav('notification-settings', 'Notifications'),
  SIGN_OUT: makeNav('logout', 'Sign Out')
});

export const AdminNav = Object.freeze({
  ADMIN_REPORTS: makeNav('admin-reports', 'CSV Reports'),
  ADMIN_ACCOUNTS: makeNav('admin-accounts', 'Accounts'),
  ADMIN_BOOKINGS: makeNav('admin-bookings', 'Bookings'),
  ADMIN_LOCATIONS: makeNav('admin-locations', 'Locations'),
  ADMIN_INVOICES: makeNav('admin-invoices', 'Invoices'),
  ADMIN_READY_FOR_PAYOUT: makeNav('admin-ready-for-payout', 'Ready For Payout'),
  ADMIN_PAYOUTS: makeNav('admin-payouts', 'Payouts'),
  ADMIN_SPACE_USAGE: makeNav('admin-space-usage', 'Space Usage'),
  ADMIN_INTERCHANGES: makeNav('admin-interchanges', 'Interchanges'),
  LOGIN_AS_ACCOUNT: makeNav('login-as-account', 'Log In As Account')
});

export const BuyerNav = Object.freeze({
  MY_BOOKINGS: makeNav('bookings', 'My Bookings', {
    badgeObservable: ObservableVariables.PENDING_APPROVAL_COUNT,
    urlParams: MenuItemUrlParams.BUYER_BOOKING
  }),
  BUYER_INVENTORY: makeNav('buyer-inventory', 'Current Inventory'),
  BUYER_INTERCHANGES: makeNav('buyer-interchanges', 'Interchanges'),
  BUYER_INVOICES: makeNav('buyer-invoices', 'Invoices'),
  BUYER_BOOKED_SPACE_CALENDAR: makeNav('buyer-booked-space-calendar', 'Booked Space Calendar'),
  BUYER_INVENTORY_CORRECTIONS: makeNav('buyer-inventory-corrections', 'Inventory Corrections'),
});

export const GateManagementNav = Object.freeze({
  CHECK_IN: makeNav('check-in', 'Check In'),
  SUPPLIER_INVENTORY: makeNav('supplier-inventory', 'Current Inventory'),
  SUPPLIER_INTERCHANGES: makeNav('supplier-interchanges', 'Interchanges'),
  SUPPLIER_ACTIVITY_SUMMARY: makeNav('supplier-activity-summary', 'Activity Summary'),
  SUPPLIER_PENDING_INVENTORY_ACTIVITY: makeNav('supplier-pending-inventory-activity', 'Pending Inventory Activity', {
    badgeObservable: ObservableVariables.PENDING_INVENTORY_ACTIVITY_COUNT
  }),
  INVENTORY_CORRECTION: makeNav('inventory-corrections', 'Inventory Corrections', {
    badgeObservable: ObservableVariables.PENDING_INVENTORY_CORRECTIONS_COUNT
  }),
});

export const SupplierNav = Object.freeze({
  GMS_LANDING: makeNav('gms-landing', 'Gate Management'),
  APPROVALS: makeNav('approvals', 'Approvals', {
    badgeObservable: ObservableVariables.PENDING_APPROVAL_COUNT
  }),
  SUPPLIER_BOOKINGS: makeNav('supplier-bookings', 'All Bookings'),
  SUPPLIER_INVOICES: makeNav('supplier-invoices', 'Invoices'),
  SUPPLIER_ACCOUNTS_RECEIVABLE: makeNav('finances/supplier-accounts-receivable', 'Accounts Receivable'),
  READY_FOR_PAYOUT: makeNav('ready-for-payout', 'Ready For Payout'),
  SUPPLIER_PAYOUTS: makeNav('supplier-payouts', 'Payouts'),
  PARTNER_SUBSCRIPTIONS: makeNav('subscriptions', 'Subscriptions'),
  SUPPLIER_BOOKED_SPACE_CALENDAR: makeNav('supplier-booked-space-calendar', 'Space Usage')
});

export const MarketplaceOnlyGmsUserNav = Object.freeze({
  GMS_UPGRADE: makeNav('gms-upgrade', "Upgrade Required")
});
